import { useCallback, useEffect, useRef, useState } from 'react';

import { useAnnouncementHeaderBanner } from '@/components/AnnouncementHeaderBanner';
import { SegmentTabs } from '@/components/SegmentTabs';
import { TabRootScenes } from '@/components/TabRootList';
import { ContentLibrary } from '@/components/contentLibrary';
import { Header } from '@/components/header';
import {
  LIBRARY_TAB_ROUTES,
  isLibraryTabNewlySelected,
  type LibraryTabKey,
} from '@/components/library.helpers';
import { TAB_HEADER_ITEMS } from '@/components/tabHeaderItems';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { Community, CommunitySavedPostsScene } from '@/features/inquiry-library';
import { segmentClient } from '@/util/analytics';

export type LibraryProps = {
  initialTab?: LibraryTabKey;
};

/**
 * Native-phone host for Inquiries, Content Posts, and Saved, switched by a segment
 * control at the top of the list the same way `components/directory.tsx` switches
 * "All Experts" and "Featured". A visited section stays mounted, so switching back is
 * instant and keeps its place. It is not a swipeable pager, because iOS collapses the large
 * title against one list only. Web keeps its own
 * separate `/community` and `/content-library` pages untouched; Saved has no
 * web equivalent route since web's saved-posts view lives inside `Community`.
 */
export function Library({ initialTab = 'inquiryLibrary' }: LibraryProps) {
  const announcementBanner = useAnnouncementHeaderBanner();

  const initialTabIndex = LIBRARY_TAB_ROUTES.findIndex((route) => route.key === initialTab);

  const [tabIndex, setTabIndex] = useState(initialTabIndex === -1 ? 0 : initialTabIndex);

  const inquiriesTabIndex = LIBRARY_TAB_ROUTES.findIndex((route) => route.key === 'inquiryLibrary');

  const goToInquiriesTab = useCallback(() => {
    setTabIndex(inquiriesTabIndex);
  }, [inquiriesTabIndex]);

  // `onIndexChange` never fires on mount, so a cold entry straight onto a
  // non-default tab needs its own tracking call here. Re-focusing an
  // already-mounted tab (e.g. via back navigation) isn't tracked separately —
  // only the first view matters for these events, same as `COMMUNITY_VIEWED`.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only; initialTab is read once, not tracked for changes
  useEffect(() => {
    if (initialTab === 'contentLibrary') {
      segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_TAB_SELECTED);
    } else if (initialTab === 'saved') {
      segmentClient.track(ANALYTICS_EVENTS.SAVED_POSTS_TAB_SELECTED);
    }
  }, []);

  /**
   * The Library tab stays mounted, so a deep link into it arrives as a new `initialTab`
   * on a screen that already chose one. Without this the link would leave the reader on
   * whichever tab they were last on.
   */
  const previousInitialTab = useRef(initialTab);

  useEffect(() => {
    if (previousInitialTab.current === initialTab) {
      return;
    }

    previousInitialTab.current = initialTab;

    const nextIndex = LIBRARY_TAB_ROUTES.findIndex((route) => route.key === initialTab);

    if (nextIndex !== -1) {
      setTabIndex(nextIndex);
    }
  }, [initialTab]);

  const handleTabIndexChange = useCallback((index: number) => {
    setTabIndex((previousIndex) => {
      if (isLibraryTabNewlySelected('contentLibrary', previousIndex, index)) {
        segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_TAB_SELECTED);
      } else if (isLibraryTabNewlySelected('saved', previousIndex, index)) {
        segmentClient.track(ANALYTICS_EVENTS.SAVED_POSTS_TAB_SELECTED);
      }

      return index;
    });
  }, []);

  const segmentTabs = (
    <SegmentTabs
      tabs={LIBRARY_TAB_ROUTES}
      selectedKey={LIBRARY_TAB_ROUTES[tabIndex]?.key ?? 'inquiryLibrary'}
      onSelect={(key) =>
        handleTabIndexChange(LIBRARY_TAB_ROUTES.findIndex((route) => route.key === key))
      }
    />
  );

  return (
    <VStack className="flex-1">
      <Header title="Library" isLarge contentScrollsUnderHeader rightItems={TAB_HEADER_ITEMS} />

      <TabRootScenes
        sceneKeys={LIBRARY_TAB_ROUTES.map((route) => route.key)}
        selectedKey={LIBRARY_TAB_ROUTES[tabIndex]?.key ?? 'inquiryLibrary'}
        lead={
          <>
            {announcementBanner}
            {segmentTabs}
          </>
        }
        renderScene={(key) => {
          if (key === 'inquiryLibrary') {
            return <Community />;
          }

          if (key === 'contentLibrary') {
            return <ContentLibrary />;
          }

          return <CommunitySavedPostsScene onViewAllPosts={goToInquiriesTab} />;
        }}
      />
    </VStack>
  );
}
